import { useQueries } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import type { LineSeriesOption, BarSeriesOption } from "echarts";
import { LineChart } from "lucide-react";
import React, { useEffect } from "react";
import { OFF } from "@/dateRanges/refreshIntervals";
import { useChartOptions } from "@/chartOptions/ChartOptionsContext";
import { getLatestTimestamp } from "@/components/charts/lineChartResponse";
import getWeekends from "@/chartOptions/getWeekends";
import { lineCharts } from "@/components/charts/api";
import { useTimeWindowHighlight } from "@/components/charts/timeWindowHighlighting";
import { ChartRef, ChartProps } from "@/components/charts/chartTypes";
import { useChartSeries } from "@/components/charts/useChartSeries";
import { resolveSeriesType } from "@/components/charts/seriesTypes";
import { buildBaseSeriesEntry } from "@/components/charts/buildBaseSeriesEntry";
import { downloadEChartsInstance } from "@/components/charts/downloadEChartsInstance";
import { normaliseChartQuery, findFirstOverrides } from "@/components/charts/resolveChartData";
import { useDynamicFontSize } from "@/components/charts/useDynamicFontSize";
import { applyDataGapVisualization } from "@/components/charts/applyDataGapVisualization";
import {
  buildOnPeriodHighlightSeries,
  buildWeekendHighlightSeries,
  buildYAxisRangeHighlightSeries,
} from "@/components/charts/buildAnnotationSeries";
import {
  buildChartGrid,
  buildChartXAxis,
  buildChartYAxis,
  buildChartLegend,
  buildChartTooltipConfig,
  chartTextStyle,
} from "@/components/charts/buildEChartsOptions";

export type { ChartRef, ChartProps };

type ChartSeriesOption = LineSeriesOption | BarSeriesOption;

const ECHARTS_GROUP_ID = 'group1';
const defaultHighlightTimePeriodsColor = 'rgba(255,145,0,0.2)';
const highlightWeekendsColor = 'rgba(161,161,161,0.2)';

export const Chart = React.forwardRef<ChartRef, ChartProps>(function Chart(props, ref) {
  const {
    title,
    height = 340,
    heightUnit = "px",
    query,
    showZoom = true,
    unit,
    apiPath = "installation",
    refreshInterval,
    isLoadingCallback,
    hasDataCallback,
    yAxisName,
    tooltip,
    canShowTemperatureOnPrimaryYAxis = false,
    clampYAxisToData = false,
    highlightTimePeriods = [],
    highlightTimePeriodsColor = defaultHighlightTimePeriodsColor,
    showPoints = false,
    hideChartOptions = false,
    trendlineData,
    combineSeries,
    highlightYAxisRange,
    enableDataGapVisualization = false,
  } = props;

  const [echartsInstance, setEchartsInstance] = React.useState<any>();
  const fontSize = useDynamicFontSize();

  const { showTemperature, temperature, highlightWeekends, temperatureUnit, highlightPowerOff } = useChartOptions();
  const effectiveShowTemperature = !hideChartOptions && showTemperature;
  const effectiveHighlightWeekends = !hideChartOptions && highlightWeekends;

  React.useEffect(() => { echarts.connect(ECHARTS_GROUP_ID); }, []);

  const queries = normaliseChartQuery(query);

  const results = useQueries({
    // @ts-ignore
    queries: queries.map((q) => ({
      ...lineCharts.detail(apiPath, q),
      refetchInterval: () => {
        if (q?.dateRange.value.live && refreshInterval !== OFF) return refreshInterval?.value;
        return false;
      },
    }))
  });

  const anyResultIsLoading = results.some((n) => n.isLoading) || (effectiveShowTemperature && temperature.isPending);
  useEffect(() => { isLoadingCallback?.(anyResultIsLoading); }, [anyResultIsLoading]);

  const concatenatedResults = useChartSeries({
    results,
    effectiveShowTemperature,
    temperature,
    temperatureUnit,
    canShowTemperatureOnPrimaryYAxis,
    combineSeries,
    trendlineData,
  });

  const { highlightWindow, isWindowScrolledOff, highlightSpecificTimeWindow, clearHighlight, createHighlightMarkArea, createInsetOverlaySeries } = useTimeWindowHighlight(concatenatedResults);

  const hasData = concatenatedResults.length > 0;
  useEffect(() => { hasDataCallback?.(hasData); }, [hasData]);

  const overrides = React.useMemo(() => findFirstOverrides(results), [results]);

  const downloadChart = (filename: string = 'chart') => {
    if (echartsInstance) downloadEChartsInstance(echartsInstance, { groupId: ECHARTS_GROUP_ID, filename });
  };

  const resize = React.useCallback(() => {
    try { echartsInstance?.resize(); } catch { /* instance may be disposed */ }
  }, [echartsInstance]);

  React.useImperativeHandle(ref, (): ChartRef => ({
    downloadChart,
    resize,
    overrides,
    highlightSpecificTimeWindow,
    clearHighlight,
    latestDataTimestamp: getLatestTimestamp(concatenatedResults),
  }), [overrides, resize, highlightSpecificTimeWindow, clearHighlight, concatenatedResults]);

  // ── Series ─────────────────────────────────────────────────────────────────

  const series = React.useMemo(() => {
    const trendSeriesNames = concatenatedResults
      .flatMap(r => r.series.map(s => s.name))
      .filter(name => resolveSeriesType(name).kind === 'trendline');

    // 1. Build base series from query data
    let built: ChartSeriesOption[] = concatenatedResults.reduce((acc: ChartSeriesOption[], r, resultIndex) => {
      const chartStart = new Date(r.series[0]?.data[0]?.[0] ?? 0).getTime();
      const insetWindow = isWindowScrolledOff && highlightWindow ? {
        insetStart: chartStart,
        insetEnd: chartStart + (highlightWindow.end - highlightWindow.start),
      } : null;

      r.series.forEach((s, seriesIndex) => {
        acc.push(buildBaseSeriesEntry(s, {
          seriesIndex,
          trendSeriesNames,
          showPoints,
          fallbackName: typeof title === 'string' ? title : undefined,
          isFirstSeries: resultIndex === 0 && acc.length === 0,
          insetWindow,
          createHighlightMarkArea,
        }));
      });
      return acc;
    }, []);

    // 2. Gap visualisation — bridges + power-off bands
    if (enableDataGapVisualization) {
      built = applyDataGapVisualization(built as LineSeriesOption[], { highlightPowerOff });
    }

    // 3. Inset overlay for time-window highlighting
    if (isWindowScrolledOff && concatenatedResults.length > 0) {
      concatenatedResults.forEach(result => {
        result.series.forEach(s => {
          const overlay = createInsetOverlaySeries(s, concatenatedResults, {
            type: s.type || 'line',
            smooth: false,
            symbol: 'none',
            lineStyle: s.lineStyle || { width: 2 },
            name: s.name,
          });
          if (overlay) built.push(overlay as LineSeriesOption);
        });
      });
    }

    // 4. Annotation overlays (mark areas)
    built.push(buildOnPeriodHighlightSeries(highlightTimePeriods, highlightTimePeriodsColor));
    built.push(buildWeekendHighlightSeries(
      effectiveHighlightWeekends && queries[0] ? getWeekends(queries[0].dateRange) : [],
      highlightWeekendsColor,
    ));
    if (highlightYAxisRange) {
      built.push(buildYAxisRangeHighlightSeries(highlightYAxisRange, fontSize));
    }

    return built;
  }, [
    concatenatedResults,
    showPoints,
    title,
    isWindowScrolledOff,
    highlightWindow,
    enableDataGapVisualization,
    highlightPowerOff,
    highlightTimePeriods,
    highlightTimePeriodsColor,
    effectiveHighlightWeekends,
    queries,
    highlightYAxisRange,
    createHighlightMarkArea,
    createInsetOverlaySeries,
    fontSize,
  ]);

  // ── ECharts options ────────────────────────────────────────────────────────

  const options = React.useMemo(() => {
    if (concatenatedResults.length === 0 || anyResultIsLoading) return {};
    return {
      series,
      grid: buildChartGrid({ showZoom }),
      xAxis: buildChartXAxis({ results: concatenatedResults, fontSize }),
      yAxis: buildChartYAxis({ results: concatenatedResults, fontSize, unit, overrides, yAxisName, clampYAxisToData }),
      legend: buildChartLegend({ results: concatenatedResults, fontSize }),
      dataZoom: showZoom ? [{ type: "slider" }, { type: "inside", zoomLock: true }] : null,
      tooltip: buildChartTooltipConfig({ showTooltip: tooltip !== false }),
      textStyle: chartTextStyle,
    };
  }, [concatenatedResults, anyResultIsLoading, series, fontSize, showZoom, unit, overrides, yAxisName, clampYAxisToData, tooltip]);

  return (
    <div style={height ? { minHeight: `${height}${heightUnit}` } : {}} className="relative">
      <ReactECharts
        onChartReady={(instance) => { instance.group = ECHARTS_GROUP_ID; setEchartsInstance(instance); }}
        option={options}
        notMerge={false}
        className="flex-1"
        style={{ position: "absolute", height: "100%", width: "100%" }}
      />
    </div>
  );
});

export const ChartLoading = ({ height }: { height?: number }) => (
  <div
    style={height ? { minHeight: `${height}px` } : {}}
    className="flex h-80 w-full flex-1 animate-pulse flex-col items-center justify-center rounded text-thalo-foreground"
  >
    <LineChart size={48} />
    <p>Loading</p>
  </div>
);

export const ChartNoData = ({ height }: { height?: number }) => (
  <div
    style={height ? { minHeight: `${height}px` } : {}}
    className="flex h-80 w-full flex-1 flex-col gap-2 items-center justify-center rounded bg-gray-50 border border-gray-200 text-gray-500 p-6"
  >
    <LineChart size={40} className="text-gray-400" />
    <p className="text-sm font-medium text-gray-600">No data to show right now</p>
    <p className="text-xs text-gray-400">Make sure the asset is powered on and reporting data.</p>
  </div>
);
